import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, ArrowRight, Package, Calendar, Truck, BadgeCheck, CreditCard, Scale, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import ItemCard from "@/components/ItemCard";
import { CATEGORIES, CARIBBEAN_LOCATIONS, ALL_LOCATIONS, locationToSlug } from "@/lib/constants";
import api from "@/lib/api";

export default function Home() {
  const navigate = useNavigate();
  const [locationQuery, setLocationQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get("/items", { params: { limit: 8 } })
      .then((res) => setItems(res.data.items))
      .catch(console.error);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    if (value.trim().length > 0) {
      const filtered = ALL_LOCATIONS.filter((loc) =>
        loc.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 6);
      setFilteredLocations(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleLocationSelect = (location) => {
    setLocationQuery("");
    setShowSuggestions(false);
    navigate(`/${locationToSlug(location.name)}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (locationQuery.trim() && filteredLocations.length > 0) {
      handleLocationSelect(filteredLocations[0]);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "name": "Paradise Borrowed",
        "url": "https://www.paradiseborrowed.com",
        "description": "Paradise Borrowed\u2122 is a peer-to-peer item borrowing marketplace that connects people who need things with neighbors who have them, across 18 US states and 8 Caribbean territories.",
        "potentialAction": { "@type": "SearchAction", "target": "https://www.paradiseborrowed.com/browse?search={search_term_string}", "query-input": "required name=search_term_string" }
      },
      {
        "@type": "Organization",
        "name": "Paradise Borrowed",
        "alternateName": "Paradise Borrowed\u2122",
        "url": "https://www.paradiseborrowed.com",
        "logo": "https://www.paradiseborrowed.com/logo.png",
        "description": "Paradise Borrowed\u2122 is a peer-to-peer item borrowing marketplace that connects people who need things with neighbors who have them, across 18 US states and 8 Caribbean territories.",
        "foundingDate": "2024",
        "legalName": "Paradise Borrowed LLC",
        "areaServed": [
          {"@type": "Country", "name": "United States"},
          {"@type": "Place", "name": "Cayman Islands"},
          {"@type": "Place", "name": "Turks and Caicos"},
          {"@type": "Place", "name": "British Virgin Islands"},
          {"@type": "Place", "name": "Bermuda"},
          {"@type": "Place", "name": "Aruba"},
          {"@type": "Place", "name": "Bahamas"},
          {"@type": "Place", "name": "Barbados"},
          {"@type": "Place", "name": "Saint Barthelemy"}
        ],
        "sameAs": []
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Paradise Borrowed™ | Borrow What You Need</title>
        <meta name="description" content="Borrow what you need from people nearby. Beach gear, baby equipment, cameras, tools & more across 18 states and the Caribbean. Paradise Borrowed™ — the peer-to-peer borrowing platform." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <p className="text-sm sm:text-base font-medium text-coral mb-3 font-ui">
                Welcome to Paradise Borrowed, the new Borrowers Economy
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-ink leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400, letterSpacing: '-0.025em' }}>
                Everything you need<br />
                <span className="text-coral">is already there</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-ink-light max-w-lg leading-relaxed font-ui">
                Borrow what you need, wherever you are. Beach gear in Grand Cayman. Camping gear in Colorado. Moving tools in NYC.
              </p>
              {/* Location Search Bar */}
              <div className="mt-8 relative" ref={searchRef} data-testid="hero-search-form">
                <div className="flex items-center bg-white rounded-full shadow-lg shadow-ink/5 border border-ink/10 p-1.5 max-w-lg hover:shadow-xl transition-shadow">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-light" />
                    <input
                      data-testid="hero-search-input"
                      type="text"
                      placeholder="Where are you going?"
                      value={locationQuery}
                      onChange={handleLocationChange}
                      onFocus={() => locationQuery.trim() && setShowSuggestions(true)}
                      className="w-full pl-12 pr-4 py-3 rounded-full text-sm bg-transparent focus:outline-none placeholder:text-ink-light font-ui"
                    />
                  </div>
                  <Button 
                    type="button" 
                    onClick={handleSearch}
                    className="rounded-full px-6 h-10 bg-coral hover:bg-[#d44814] text-white btn-press font-ui" 
                    data-testid="hero-search-btn"
                  >
                    Search
                  </Button>
                </div>
                {showSuggestions && filteredLocations.length > 0 && (
                  <div className="absolute top-full left-0 right-0 max-w-lg mt-2 bg-white rounded-xl shadow-lg border border-ink/10 overflow-hidden z-50">
                    {filteredLocations.map((loc) => (
                      <button
                        key={loc.name}
                        type="button"
                        onClick={() => handleLocationSelect(loc)}
                        className="w-full px-4 py-3 text-left hover:bg-coral-light flex items-center gap-3 transition-colors font-ui"
                      >
                        <MapPin className="w-4 h-4 text-ink-light" />
                        <span className="text-sm text-ink">{loc.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {CARIBBEAN_LOCATIONS.slice(0, 4).map((loc) => (
                  <button
                    key={loc.name}
                    onClick={() => navigate(`/${locationToSlug(loc.name)}`)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-ink/10 text-ink-light hover:border-coral hover:text-coral hover:bg-coral-light transition-all cursor-pointer font-ui"
                    data-testid={`quick-dest-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-4 animate-fade-in stagger-2">
              <div className="space-y-4">
                <img src="https://static.prod-images.emergentagent.com/jobs/af823ad1-a1f5-4bed-82f0-1044b4ad72e4/images/2b35fb71c43c24ba7edb1a9992d51b60dd13aa53336c5998cca78a65e189359d.png" alt="Tropical beach with paddleboards" className="rounded-2xl w-full h-48 object-cover shadow-md" />
                <img src="https://static.prod-images.emergentagent.com/jobs/af823ad1-a1f5-4bed-82f0-1044b4ad72e4/images/15b3531e41d0cc81e93aff843bb8efd6605dbaf82c3c3e2f37d003f0b6524463.png" alt="Baby car seat in car" className="rounded-2xl w-full h-36 object-cover shadow-md" />
              </div>
              <div className="space-y-4 pt-8">
                <img src="https://static.prod-images.emergentagent.com/jobs/af823ad1-a1f5-4bed-82f0-1044b4ad72e4/images/f0184a9f54102071b89fb131556f8f41187268ec11f1d2d9812d50fa07e5ccdc.png" alt="Colorado camping gear" className="rounded-2xl w-full h-36 object-cover shadow-md" />
                <img src="https://static.prod-images.emergentagent.com/jobs/af823ad1-a1f5-4bed-82f0-1044b4ad72e4/images/2ae199aaaaeaf6d537f76f56abc557adcd517ad64185c521658619c48d1b4e57.png" alt="NYC moving dolly" className="rounded-2xl w-full h-48 object-cover shadow-md" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-b border-ink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {/* Verified Users */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-coral-light flex items-center justify-center shrink-0">
                <BadgeCheck className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-medium text-sm text-ink font-ui">Verified Users</h3>
                <p className="text-xs text-ink-light mt-0.5 leading-relaxed font-ui">
                  Email-verified members you can trust
                </p>
              </div>
            </div>

            {/* Secure Payments */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-light flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5 text-teal" />
              </div>
              <div>
                <h3 className="font-medium text-sm text-ink font-ui">Secure Payments</h3>
                <p className="text-xs text-ink-light mt-0.5 leading-relaxed font-ui">
                  Verified accounts
                </p>
              </div>
            </div>

            {/* Dispute Resolution */}
            <Link to="/trust" className="flex items-start gap-3 hover:bg-sand rounded-lg p-2 -m-2 transition-colors">
              <div className="w-10 h-10 rounded-full bg-coral-light flex items-center justify-center shrink-0">
                <Scale className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h3 className="font-medium text-sm text-ink font-ui">Dispute Resolution</h3>
                <p className="text-xs text-ink-light mt-0.5 leading-relaxed font-ui">
                  Fair mediation for every booking
                </p>
              </div>
            </Link>

            {/* Transparent Reviews */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FDF6E3] flex items-center justify-center shrink-0">
                <Star className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-medium text-sm text-ink font-ui">Transparent Reviews</h3>
                <p className="text-xs text-ink-light mt-0.5 leading-relaxed font-ui">
                  Honest ratings from real users
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-white border-y border-ink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.name;
              return (
                <button
                  key={cat.name}
                  onClick={() => {
                    setActiveCategory(isActive ? null : cat.name);
                    navigate(`/browse?category=${encodeURIComponent(cat.name)}`);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm whitespace-nowrap transition-all cursor-pointer shrink-0 font-ui ${
                    isActive
                      ? "bg-ink text-white border-ink"
                      : "bg-white border-ink/10 text-ink-light hover:border-coral hover:text-coral hover:bg-coral-light"
                  }`}
                  data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Items */}
      <section className="py-16 sm:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-display font-light text-2xl sm:text-3xl text-ink tracking-tight">Featured Items</h2>
              <p className="text-ink-light mt-1 text-sm font-ui">Available for borrowing right now</p>
            </div>
            <Link to="/browse" className="text-coral text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all font-ui" data-testid="view-all-items">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* Caribbean Destinations */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="font-display font-light text-2xl sm:text-3xl text-ink tracking-tight">Popular Destinations</h2>
            <p className="text-ink-light mt-1 text-sm font-ui">Browse items available in these Caribbean islands</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {CARIBBEAN_LOCATIONS.map((loc) => (
              <Link
                key={loc.name}
                to={`/${locationToSlug(loc.name)}`}
                className="group relative rounded-2xl overflow-hidden aspect-[3/2] img-zoom"
                data-testid={`destination-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <img src={loc.image} alt={loc.name} className="w-full h-full object-cover transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 p-4">
                  <div className="flex items-center gap-1.5 text-white">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="font-display font-light text-sm">{loc.name}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-light text-2xl sm:text-3xl text-ink text-center mb-12 tracking-tight">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Package, title: "Find It", desc: "Browse thousands of items available near your destination or in your neighborhood." },
              { icon: Calendar, title: "Book It", desc: "Check availability, select your dates, and reserve with a few clicks." },
              { icon: Truck, title: "Get It", desc: "Pick up the item or get it delivered right to your door." },
            ].map((step, i) => (
              <div key={step.title} className="text-center p-8 rounded-2xl bg-white border border-ink/10 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-coral-light flex items-center justify-center">
                  <step.icon className="w-7 h-7 text-coral" />
                </div>
                <h3 className="font-display font-light text-lg text-ink mb-2">{step.title}</h3>
                <p className="text-ink-light text-sm leading-relaxed font-ui">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 bg-teal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-light text-3xl sm:text-4xl text-white mb-4 tracking-tight">Have Items to Lend?</h2>
          <p className="text-white/80 text-base max-w-md mx-auto mb-8 font-ui">
            Turn your idle items into income. List your gear, set your price, and start earning.
          </p>
          <Link to="/create-listing">
            <Button size="lg" className="rounded-full px-8 bg-white hover:bg-sand text-teal font-medium btn-press text-base font-ui" data-testid="cta-lend-items">
              Start Lending
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
}
